// src/components/rewards/tabs/EarnPointsTab.jsx - PRODUCTION READY
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Target, Users, Clock, CheckCircle, FileText, 
  Heart, Lightbulb, Crown, Database, RefreshCw, AlertTriangle
} from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
// import useRealTimeHealthData from '../../../hooks/useRealTimeHealthData'; // Ready for Smart Medical integration
import { useMediCureOnData } from '../../../hooks/useMediCureOnData';
import useIntelligentHealthData from '../../../hooks/useIntelligentHealthData'; // Fix: Default export
import medipointsApiService from '../../../services/medipointsApiService';

const EarnPointsTab = ({ userData, onServiceClick, showNotification, updateUserData, onNavigate }) => {
  // PRODUCTION: Real authentication context
  const { user, userInfo, getAccessToken } = useAuth();
  // Note: Real-time health data integration ready for Smart Medical device connections
  // const { healthData, isLoading: healthDataLoading, refreshData } = useRealTimeHealthData();
  
  // 🌟 ENTERPRISE MEDIPOINTS SYSTEM
  const { 
    mediPointsBalance, 
    addMediPointsForDailyActivity, 
    fetchMediPoints,
    isBackendConnected: enterpriseBackendConnected 
  } = useMediCureOnData();
  
  // 🚀 REAL DEVICE DATA: Use the same hook that's working in LandingPage
  const { 
    healthData, 
    isLoading: healthDataLoading
  } = useIntelligentHealthData();
  
  // 🔍 DEBUG: Log health data to verify it's working
  useEffect(() => {
    if (healthData) {
      console.log('🎯 EARN POINTS: Health data available:', {
        devices: healthData.devices?.length || 0,
        activity: healthData.activity,
        vitals: healthData.vitals,
        wellness: healthData.wellness
      });
    }
  }, [healthData]);
  
  // Real user identification from auth context
  const userId = user?.localAccountId || user?.username || userInfo?.localAccountId || 'temp_user_id';
  const userCountry = userInfo?.country || userInfo?.personalDetails?.country || user?.idTokenClaims?.country || 'United States';

  // 🚀 MODERNIZED: Production Daily Activities with Dynamic IoMT Device Data Integration
  const [dailyActivities, setDailyActivities] = useState([
    {
      id: 'smart_scale_weigh_in',
      name: 'Body Composition Analysis',
      description: 'Automatic weight tracking from connected smart scales',
      detailedDescription: 'Advanced weight trend analysis with automatic point awarding when daily weigh-in detected',
      icon: '⚖️',
      deviceIcon: '📊',
      points: 10,
      claimedToday: false,
      dataAvailable: false, // Updated automatically via IoMT data sync
      currentValue: 'Waiting for device sync...',
      requirement: 'Daily weigh-in automatically detected from connected smart scale',
      dataSources: ['Withings Body+', 'iPhone HealthKit', 'Garmin Index S2', 'Fitbit Aria Air'],
      bonus: 'Automatic BMI tracking and comprehensive body composition analysis',
      category: 'Body Metrics',
      dataType: 'weight',
      validationRules: {
        minValue: 30, // kg - minimum realistic weight
        maxValue: 300, // kg - maximum realistic weight
        requiresToday: true,
        maxClaimsPerDay: 1,
        deviceRequired: true, // 🔒 ANTI-GAMING: Device required for points
        automaticDetection: true // 🚀 NEW: Auto-award when data detected
      },
      lastDataTimestamp: null,
      deviceConnected: false,
      isDynamic: true // 🚀 NEW: Mark as dynamic for automatic processing
    },
    {
      id: 'blood_pressure_logging',
      name: 'Cardiovascular Risk Assessment',
      description: 'Coming Soon in Phase 02 - Advanced cardiac health monitoring',
      detailedDescription: 'Professional-grade cardiovascular risk assessment with smart blood pressure monitoring devices',
      icon: '🩺',
      deviceIcon: '💓',
      points: 10,
      claimedToday: false,
      dataAvailable: false,
      currentValue: 'Feature coming in Phase 02...',
      requirement: 'Smart blood pressure device integration (Withings BPM Connect, Omron HeartGuide)',
      dataSources: ['Omron HeartGuide', 'Withings BPM Connect', 'Microlife BP A200'],
      bonus: 'Real-time cardiovascular risk assessment and personalized heart health insights',
      category: 'Cardiovascular',
      dataType: 'blood_pressure',
      validationRules: {
        systolicRange: [70, 200], // mmHg
        diastolicRange: [40, 130], // mmHg
        requiresToday: true,
        maxClaimsPerDay: 1,
        deviceRequired: true, // 🔒 ANTI-GAMING: Device required for points
        automaticDetection: true
      },
      lastDataTimestamp: null,
      deviceConnected: false,
      isDynamic: false, // 🚀 DISABLED: Coming Soon in Phase 02
      isComingSoon: true, // 🚀 NEW: Mark as coming soon
      comingSoonPhase: 'Phase 02'
    },
    {
      id: 'hydration_tracking',
      name: 'Smart Hydration Monitoring',
      description: 'Automatic hydration tracking from iPhone HealthKit',
      detailedDescription: 'Advanced hydration analysis with automatic point awarding when daily water intake goal achieved',
      icon: '�‍♀️',
      deviceIcon: '📱',
      points: 10,
      claimedToday: false,
      dataAvailable: false,
      currentValue: 'Waiting for device sync...',
      requirement: 'Achieve 8+ glasses of water daily (automatically detected from iPhone HealthKit)',
      dataSources: ['iPhone HealthKit', 'Apple Watch', 'MyFitnessPal', 'WaterMinder App'],
      bonus: 'Personalized hydration recommendations and dehydration risk assessment',
      category: 'Hydration & Wellness',
      dataType: 'hydration',
      validationRules: {
        minGlasses: 8, // Daily water goal - 8 glasses (1920ml)
        maxGlasses: 20, // Reasonable maximum
        requiresToday: true,
        maxClaimsPerDay: 1,
        deviceRequired: true, // 🔒 ANTI-GAMING: Device required for points
        automaticDetection: true // 🚀 NEW: Auto-award when goal achieved
      },
      lastDataTimestamp: null,
      deviceConnected: false,
      isDynamic: true // 🚀 NEW: Mark as dynamic for automatic processing
    },
    {
      id: 'healthy_meal_logging',
      name: 'Nutritional Analytics',
      description: 'Coming Soon in Phase 02 - Advanced nutrition tracking',
      detailedDescription: 'Future: Advanced meal recognition, macro tracking, and nutritional analysis with automatic point awarding',
      icon: '🥗',
      deviceIcon: '📸',
      points: 10,
      claimedToday: false,
      dataAvailable: false,
      currentValue: 'Feature coming in Phase 02...',
      requirement: 'Advanced nutrition tracking with intelligent meal recognition (Phase 02)',
      dataSources: ['Coming Soon: Smart Food Recognition', 'MyFitnessPal API', 'Nutritionix API', 'Smart Kitchen Scales'],
      bonus: 'Future: Real-time nutritional analysis and personalized meal recommendations',
      category: 'Nutrition',
      dataType: 'nutrition',
      validationRules: {
        requiresToday: false,
        maxClaimsPerDay: 1,
        deviceRequired: true,
        automaticDetection: false,
        comingSoon: true // 🚧 NEW: Phase 02 feature flag
      },
      lastDataTimestamp: null,
      deviceConnected: false,
      isDynamic: false, // 🚧 Will be dynamic in Phase 02
      isComingSoon: true // 🚧 NEW: Phase 02 feature
    },
    {
      id: 'sleep_data_review',
      name: 'Circadian Rhythm Analysis',
      description: 'Automatic sleep tracking from wearable devices',
      detailedDescription: 'Continuous sleep analysis with automatic point awarding for quality sleep (6+ hours)',
      icon: '😴',
      deviceIcon: '🌙',
      points: 10,
      claimedToday: false,
      dataAvailable: false,
      currentValue: 'Waiting for device sync...',
      requirement: 'Quality sleep (6+ hours) automatically detected from wearable device',
      dataSources: ['Apple Watch', 'Oura Ring', 'iPhone HealthKit', 'Withings Sleep Mat', 'Fitbit Sense'],
      bonus: 'Advanced sleep stage analysis and personalized sleep optimization recommendations',
      category: 'Sleep Quality',
      dataType: 'sleep',
      validationRules: {
        minHours: 6, // Minimum hours for points awarding
        maxHours: 12,
        requiresPreviousNight: true,
        maxClaimsPerDay: 1,
        deviceRequired: true, // 🔒 ANTI-GAMING: Device required for points
        automaticDetection: true // 🚀 NEW: Auto-award when quality sleep detected
      },
      lastDataTimestamp: null,
      deviceConnected: false,
      isDynamic: true // 🚀 NEW: Mark as dynamic for automatic processing
    },
    {
      id: 'heart_rate_variability',
      name: 'Autonomic Nervous System Assessment',
      description: 'Automatic HRV monitoring from advanced wearables',
      detailedDescription: 'Daily stress and recovery assessment with automatic point awarding for HRV measurement',
      icon: '💗',
      deviceIcon: '�',
      points: 10,
      claimedToday: false,
      dataAvailable: false,
      currentValue: 'Waiting for device sync...',
      requirement: 'Daily HRV measurement automatically detected from compatible wearable',
      dataSources: ['Apple Watch', 'iPhone HealthKit', 'Oura Ring', 'Polar H10', 'WHOOP Strap'],
      bonus: 'Advanced stress pattern recognition and personalized recovery recommendations',
      category: 'Heart Health',
      dataType: 'hrv',
      validationRules: {
        minHRV: 20, // ms - minimum realistic HRV for points (lowered from 30 to be more achievable)
        maxHRV: 200, // ms - maximum realistic HRV
        requiresToday: true,
        maxClaimsPerDay: 1,
        deviceRequired: true, // 🔒 ANTI-GAMING: Device required for points
        automaticDetection: true // 🚀 NEW: Auto-award when HRV data detected
      },
      lastDataTimestamp: null,
      deviceConnected: false,
      isDynamic: true // 🚀 NEW: Mark as dynamic for automatic processing
    }
  ]);

  // Production State Management for MediPoints (MediCureOn Data Integration)
  const [claimingStates, setClaimingStates] = useState({});
  const [totalPointsEarnedToday, setTotalPointsEarnedToday] = useState(0);
  // 🌟 ENTERPRISE: Use enterprise MediPoints balance directly
  const totalMediPoints = mediPointsBalance.currentBalance || 0; // Enterprise system balance
  const [isBackendConnected, setIsBackendConnected] = useState(enterpriseBackendConnected); // Enterprise connection status

  // Test Backend Connectivity with simple API call
  const testBackendConnectivity = useCallback(async () => {
    if (!userId || userId === 'temp_user_id') return false;
    
    try {
      console.log(`🔍 Testing backend connectivity for user ${userId}`);
      
      // Simple balance check to test connectivity
      const response = await medipointsApiService.getUserBalance(userId, userCountry);
      
      if (response && response.success) {
        console.log(`✅ Backend connectivity confirmed - Balance: ${response.data.currentBalance}`);
        setIsBackendConnected(true);
        return true;
      } else {
        console.warn('⚠️ Backend connectivity test failed:', response?.error);
        setIsBackendConnected(false);
        return false;
      }
      
    } catch (error) {
      console.warn('⚠️ Backend connectivity test error:', error.message);
      setIsBackendConnected(false);
      return false;
    }
  }, [userId, userCountry]);

  // Load Total MediPoints from MediCureOn Data on component mount
  const loadUserMediPoints = useCallback(async () => {
    if (!userId || userId === 'temp_user_id') return;
    
    try {
      console.log(`💰 Loading MediPoints for user ${userId} from ${userCountry} via Enterprise system`);
      
      // Test connectivity first
      const isConnected = await testBackendConnectivity();
      
      if (isConnected) {
        // Use enterprise MediPoints system to fetch latest balance
        const result = await fetchMediPoints();
        
        if (result && result.success) {
          setIsBackendConnected(true);
          console.log(`☁️ Enterprise MediPoints system connected, balance: ${result.data.currentBalance || 0} points`);
        } else {
          console.warn('⚠️ Enterprise MediPoints system not available, but backend is connected');
          // Keep connection status as true since basic connectivity works
        }
      } else {
        console.warn('⚠️ Backend connectivity failed, will retry');
        setIsBackendConnected(false);
      }
      
    } catch (error) {
      console.error('❌ Error connecting to Enterprise MediPoints system:', error);
      setIsBackendConnected(false);
    }
  }, [userId, userCountry, fetchMediPoints, testBackendConnectivity]);

  // Save MediPoints to MediCureOn Data Storage using Enterprise system
  const saveMediPointsToBackend = useCallback(async (pointsToAdd, activityName, metadata = {}) => {
    if (!userId || userId === 'temp_user_id') {
      console.warn('❌ Invalid user ID for MediPoints save');
      return false;
    }
    
    try {
      console.log(`🌟 ENTERPRISE: Adding ${pointsToAdd} MediPoints via centralized system: ${activityName}`);
      console.log(`🔍 User: ${userId}, Country: ${userCountry}`);
      
      // Use the new enterprise MediPoints system via useMediCureOnData hook
      const result = await addMediPointsForDailyActivity(
        pointsToAdd,    // amount (number)
        activityName,   // activityType (string)
        metadata.dataType || 'health activity', // dataType (string)
        {
          deviceConnected: metadata.deviceConnected,
          deviceValidated: metadata.deviceValidated,
          manualEntry: metadata.manualEntry,
          source: 'EarnPointsTab',
          ...metadata
        }
      );
      
      if (result.success) {
        console.log('✅ Enterprise MediPoints system response:', result.data);
        
        // Update connection status since API call succeeded
        setIsBackendConnected(true);
        
        // Enterprise system manages balance - no local state update needed
        // Balance will be updated automatically via mediPointsBalance hook
        
        // Update connection status since API call succeeded
        setIsBackendConnected(true);
        
        console.log(`💰 New MediPoints Balance: ${result.data.newBalance}, Tier: ${result.data.newTier}`);
        return true;
      } else {
        console.error('❌ Enterprise MediPoints system error:', result.error);
        setIsBackendConnected(false);
        return false;
      }
      
    } catch (error) {
      console.error('❌ Error with Enterprise MediPoints system:', error);
      setIsBackendConnected(false);
      return false;
    }
  }, [userId, userCountry, addMediPointsForDailyActivity]);

  // Load MediPoints from Enterprise system
  const loadMediPointsFromBackend = useCallback(async () => {
    if (!userId || userId === 'temp_user_id') {
      console.warn('❌ Invalid user ID for MediPoints load');
      setIsBackendConnected(false);
      return;
    }
    
    try {
      console.log('🌟 ENTERPRISE: Loading MediPoints from centralized system...');
      
      // Use enterprise system to fetch current balance
      await fetchMediPoints();
      
      // Update backend connection status from enterprise data
      setIsBackendConnected(enterpriseBackendConnected);
      
      console.log(`✅ Enterprise MediPoints loaded successfully`);
      
    } catch (error) {
      console.error('❌ Error loading from Enterprise MediPoints system:', error);
      setIsBackendConnected(false);
    }
  }, [userId, fetchMediPoints, enterpriseBackendConnected]);

  // 🌟 ENTERPRISE: Sync backend connection status with enterprise system
  useEffect(() => {
    setIsBackendConnected(enterpriseBackendConnected);
    console.log(`🔄 Enterprise MediPoints system status: ${enterpriseBackendConnected ? 'Connected' : 'Disconnected'}`);
  }, [enterpriseBackendConnected]);

  // 🔄 ENTERPRISE: Update connection status when MediPoints operations succeed
  useEffect(() => {
    if (mediPointsBalance && mediPointsBalance.currentBalance >= 0) {
      setIsBackendConnected(true);
      console.log(`✅ Enterprise connection verified - Balance: ${mediPointsBalance.currentBalance}, Tier: ${mediPointsBalance.tier}`);
    }
  }, [mediPointsBalance]);

  // 🌟 ENTERPRISE: Initialize MediPoints from enterprise system on mount
  useEffect(() => {
    if (userId && userId !== 'temp_user_id') {
      loadMediPointsFromBackend();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]); // Only depend on userId, not the function itself

  // Initialize midnight reset system for claims
  useEffect(() => {
    const checkMidnightReset = () => {
      const now = new Date();
      const today = now.toDateString();
      const lastReset = localStorage.getItem(`lastClaimReset_${userId}`);
      
      if (lastReset !== today) {
        // Reset all claims at midnight
        setDailyActivities(prev => prev.map(activity => ({
          ...activity,
          claimedToday: false
        })));
        
        localStorage.setItem(`lastClaimReset_${userId}`, today);
        console.log('🌙 Midnight reset: All daily activities available for claiming');
        if (showNotification) {
          showNotification('Daily activities have been reset! 🌟');
        }
      }
    };
    
    // Check immediately and then every minute
    checkMidnightReset();
    const interval = setInterval(checkMidnightReset, 60000);
    
    return () => clearInterval(interval);
  }, [userId, showNotification]);

  // 🔒 PRODUCTION: Load backend-validated claim states on component mount
  useEffect(() => {
    if (!userId) return;
    
    const loadDailyClaimsFromBackend = async () => {
      try {
        console.log('🔍 Loading today\'s claims from backend validation system...');
        
        // Use imported service to check daily claims
        const dailyClaimsResult = await medipointsApiService.getDailyClaimedActivities(userId, userCountry);
        
        if (dailyClaimsResult.success) {
          const todaysClaims = dailyClaimsResult.data.claimedActivities;
          console.log(`✅ Backend validation: ${todaysClaims.length} activities claimed today:`, todaysClaims);
          
          // Update activities based on backend validation
          setDailyActivities(prev => {
            const updated = prev.map(activity => ({
              ...activity,
              claimedToday: todaysClaims.includes(activity.id)
            }));
            
            // Calculate points earned today from backend data
            const pointsToday = updated.filter(activity => todaysClaims.includes(activity.id))
              .reduce((total, activity) => total + activity.points, 0);
            setTotalPointsEarnedToday(pointsToday);
            
            console.log(`💰 Total points earned today: ${pointsToday}`);
            
            return updated;
          });
        } else {
          console.warn('⚠️ Could not load backend claims, falling back to localStorage:', dailyClaimsResult.error);
          
          // Fallback to localStorage if backend fails
          const today = new Date().toDateString();
          const storedClaims = localStorage.getItem(`dailyClaims_${userId}_${today}`);
          
          if (storedClaims) {
            try {
              const claims = JSON.parse(storedClaims);
              setDailyActivities(prev => prev.map(activity => ({
                ...activity,
                claimedToday: claims[activity.id] || false
              })));
              
              // Calculate points earned today
              const pointsToday = Object.values(claims).filter(Boolean).length * 10;
              setTotalPointsEarnedToday(pointsToday);
            } catch (error) {
              console.warn('Error loading stored claims from localStorage:', error);
            }
          }
        }
      } catch (error) {
        console.error('❌ Error loading daily claims:', error);
        
        // Fallback to localStorage on error
        const today = new Date().toDateString();
        const storedClaims = localStorage.getItem(`dailyClaims_${userId}_${today}`);
        
        if (storedClaims) {
          try {
            const claims = JSON.parse(storedClaims);
            setDailyActivities(prev => prev.map(activity => ({
              ...activity,
              claimedToday: claims[activity.id] || false
            })));
          } catch (error) {
            console.warn('Error loading stored claims from localStorage fallback:', error);
          }
        }
      }
    };
    
    loadDailyClaimsFromBackend();
  }, [userId, userCountry]);


  // 🚀 DYNAMIC DEVICE DATA INTEGRATION: Real-time health monitoring system
  
  // � REAL DATA PROCESSORS: Convert IoMT data lake responses to activity format
  const processWithingsScaleData = useCallback((scaleDataResult) => {
    try {
      console.log('🔍 WEIGHT DEBUG: Processing scale data result:', scaleDataResult);
      
      if (scaleDataResult.status !== 'fulfilled' || !scaleDataResult.value?.success) {
        console.log('🔍 WEIGHT DEBUG: API call failed or unsuccessful:', scaleDataResult);
        return { connected: false, displayValue: 'Scale not connected', meetsGoal: false, deviceSource: 'Unknown' };
      }
      
      const data = scaleDataResult.value.data;
      console.log('🔍 WEIGHT DEBUG: API returned data array:', data);
      console.log('🔍 WEIGHT DEBUG: Data length:', data ? data.length : 0);
      
      if (!data || !Array.isArray(data) || data.length === 0) {
        console.log('🔍 WEIGHT DEBUG: No data array found or empty array');
        return { connected: true, displayValue: 'No recent weigh-ins from scale', meetsGoal: false, deviceSource: 'Unknown' };
      }
      
      // Get latest weight measurement and detect device source
      const latestMeasurement = data[0];
      console.log('🔍 WEIGHT DEBUG: Raw measurement data:', latestMeasurement);
      console.log('🔍 WEIGHT DEBUG: Full data object keys:', Object.keys(latestMeasurement));
      console.log('🔍 WEIGHT DEBUG: healthMetrics content:', latestMeasurement.healthMetrics);
      console.log('🔍 WEIGHT DEBUG: data content:', latestMeasurement.data);
      console.log('🔍 WEIGHT DEBUG: timestamp:', latestMeasurement.timestamp);
      console.log('🔍 WEIGHT DEBUG: Raw JSON string of entire object:', JSON.stringify(latestMeasurement, null, 2));
      console.log('🔍 WEIGHT DEBUG: Checking for health data in different locations...');
      
      // 🔧 COMPREHENSIVE WEIGHT DATA SEARCH: Look for weight data in all possible locations
      const weightSearchLocations = [
        // Withings Body Scan structure
        () => latestMeasurement.healthMetrics?.bodyComposition?.weight,
        // Apple HealthKit structure  
        () => latestMeasurement.healthMetrics?.bodyMeasurements?.weight,
        () => latestMeasurement.data?.health?.weight,
        () => latestMeasurement.data?.metrics?.weight,
        () => latestMeasurement.healthMetrics?.weight,
        () => latestMeasurement.data?.bodyComposition?.weight,
        // Legacy formats
        () => latestMeasurement.data?.healthMetrics?.bodyComposition?.weight,
        () => latestMeasurement.data?.measurements?.weight,
        () => latestMeasurement.measurements?.weight
      ];

      let weight = null;
      let deviceSource = 'Unknown Scale';

      for (const locationFn of weightSearchLocations) {
        const foundWeight = locationFn();
        if (foundWeight !== undefined && foundWeight !== null && !isNaN(foundWeight)) {
          weight = parseFloat(foundWeight);
          console.log('🔍 WEIGHT DEBUG: Found weight data:', weight, 'kg');
          break;
        }
      }

      // Also check for BMI data from body composition  
      let bmi = null;
      if (latestMeasurement.healthMetrics?.bodyComposition?.bmi) {
        bmi = parseFloat(latestMeasurement.healthMetrics.bodyComposition.bmi);
        console.log('🔍 WEIGHT DEBUG: Found BMI data:', bmi);
      }
      
      // � DEVICE DETECTION: Extract device info from metadata
      const deviceInfo = latestMeasurement.data?.metadata?.deviceInfo;
      if (deviceInfo) {
        // CRITICAL: Apple Watches CANNOT measure weight - must be from scale
        const detectedBrand = deviceInfo.deviceBrand || deviceInfo.brand || '';
        const detectedModel = deviceInfo.deviceModel || deviceInfo.model || '';
        
        if (detectedBrand.toLowerCase().includes('apple') && detectedModel.toLowerCase().includes('watch')) {
          deviceSource = 'Withings Body+'; // Apple Watch cannot measure weight
          console.log('🔍 WEIGHT DEBUG: Apple Watch detected but weight requires scale, using:', deviceSource);
        } else if (detectedBrand.toLowerCase().includes('withings') || 
                   detectedBrand.toLowerCase().includes('fitbit') ||
                   detectedBrand.toLowerCase().includes('scale')) {
          deviceSource = `${detectedBrand} ${detectedModel}`.trim() || 'Smart Scale';
          console.log('🔍 WEIGHT DEBUG: Scale device detected:', deviceSource);
        } else {
          deviceSource = 'Withings Body+'; // Default to scale for weight
          console.log('🔍 WEIGHT DEBUG: Unknown device for weight, defaulting to scale:', deviceSource);
        }
      }

      // ❌ NO DEMO DATA: Only use real device measurements
      // If no real weight data found, return disconnected status
      if (!weight) {
        console.log('🔍 WEIGHT DEBUG: No real weight data found - NO DEMO FALLBACK');
        return { 
          connected: false, 
          displayValue: 'No weight data available today', 
          meetsGoal: false, 
          deviceSource: deviceSource || 'Unknown' 
        };
      }

      // ✅ SUCCESS: Found real weight data
      console.log('🔍 WEIGHT DEBUG: Successfully found weight:', weight, 'kg from device:', deviceSource);
      const timestamp = latestMeasurement.timestamp || new Date().toISOString();
      const isToday = new Date(timestamp).toDateString() === new Date().toDateString();
      
      return {
        connected: true,
        value: weight,
        timestamp: timestamp,
        deviceSource: deviceSource,
        meetsGoal: isToday, // Goal is to weigh-in daily
        displayValue: isToday ? 
          `${weight.toFixed(1)} kg from ${deviceSource} ✅` : 
          `Last: ${weight.toFixed(1)} kg from ${deviceSource} (${new Date(timestamp).toLocaleDateString()})`
      };
    } catch (error) {
      console.error('Error processing scale data:', error);
    }
    
    return { connected: false, displayValue: 'Scale data processing error', meetsGoal: false, deviceSource: 'Unknown' };
  }, []);

  const processWithingsBPMData = useCallback((bpmDataResult) => {
    try {
      console.log('🔍 BP DEBUG: Processing BP data result:', bpmDataResult);
      
      if (bpmDataResult.status !== 'fulfilled' || !bpmDataResult.value?.success) {
        console.log('🔍 BP DEBUG: API call failed or unsuccessful:', bpmDataResult);
        return { connected: false, displayValue: 'BP device not connected', meetsGoal: false, deviceSource: 'Unknown' };
      }
      
      const data = bpmDataResult.value.data;
      console.log('🔍 BP DEBUG: API returned data array:', data);
      console.log('🔍 BP DEBUG: Data length:', data ? data.length : 0);
      
      if (!data || !Array.isArray(data) || data.length === 0) {
        console.log('🔍 BP DEBUG: No data array found or empty array');
        return { connected: true, displayValue: 'No recent BP measurements', meetsGoal: false, deviceSource: 'Unknown' };
      }
      
      // Get latest BP measurement and check multiple data formats
      const latestMeasurement = data[0];
      let bp = null;
      let deviceSource = 'Unknown BP Device';
      
      console.log('🔍 BP DEBUG: Raw measurement data:', latestMeasurement);
      console.log('🔍 BP DEBUG: Full data object keys:', Object.keys(latestMeasurement));
      console.log('🔍 BP DEBUG: healthMetrics content:', latestMeasurement.healthMetrics);
      console.log('🔍 BP DEBUG: data content:', latestMeasurement.data);
      
      // � DEVICE DETECTION: Extract device info from metadata
      const deviceInfo = latestMeasurement.data?.metadata?.deviceInfo;
      if (deviceInfo) {
        // BP can come from Apple Watch Series 6+ or dedicated BP monitors
        const detectedBrand = deviceInfo.deviceBrand || deviceInfo.brand || '';
        const detectedModel = deviceInfo.deviceModel || deviceInfo.model || '';
        deviceSource = `${detectedBrand} ${detectedModel}`.trim() || deviceInfo.platform || 'BP Monitor';
        console.log('🔍 BP DEBUG: Device detected:', deviceSource);
      }
      
      // 🔧 COMPREHENSIVE BP DATA SEARCH: Look for BP data in all possible locations
      const searchLocations = [
        // Withings BPM Connect structure (actual generator format)
        latestMeasurement.healthMetrics?.cardiovascular,
        // Apple Watch BP structure
        latestMeasurement.healthMetrics?.vitalSigns?.bloodPressure,
        // Samsung Health structure  
        latestMeasurement.healthMetrics?.vitalSigns,
        // Legacy bloodPressure structures
        latestMeasurement.healthMetrics?.bloodPressure,
        latestMeasurement.healthMetrics?.vitals?.bloodPressure,
        latestMeasurement.cardiovascular?.bloodPressure,
        latestMeasurement.data?.healthMetrics?.bloodPressure,
        latestMeasurement.data?.vitals?.bloodPressure,
        latestMeasurement.data?.cardiovascular?.bloodPressure,
        latestMeasurement.metrics?.bloodPressure,
        latestMeasurement.bloodPressure,
        latestMeasurement.data?.bloodPressure
      ];
      
      // Find the first valid BP measurement
      for (const location of searchLocations) {
        // Check for Withings BPM Connect format (systolicPressure/diastolicPressure)
        if (location && (location.systolicPressure || location.diastolicPressure)) {
          bp = {
            systolic: location.systolicPressure,
            diastolic: location.diastolicPressure,
            heartRate: location.heartRate
          };
          console.log('🔍 BP DEBUG: Found Withings BPM Connect format BP data:', bp);
          break;
        }
        // Check for standard format (systolic/diastolic)
        else if (location && (location.systolic || location.diastolic)) {
          bp = location;
          console.log('🔍 BP DEBUG: Found standard format BP data:', bp);
          break;
        }
      }
      
      // ❌ NO DEMO DATA: Only use real device measurements
      // If no real BP data found, return disconnected status
      if (!bp) {
        console.log('🔍 BP DEBUG: No real blood pressure data found - NO DEMO FALLBACK');
        return { 
          connected: false, 
          displayValue: 'No blood pressure data available today', 
          meetsGoal: false, 
          deviceSource: deviceSource || 'Unknown' 
        };
      }
      
      console.log('🔍 BP DEBUG: Extracted BP data:', bp, 'from device:', deviceSource);
      
      if (bp && bp.systolic && bp.diastolic) {
        const timestamp = latestMeasurement.timestamp || new Date().toISOString();
        const measurementDate = new Date(timestamp);
        const today = new Date();
        const isToday = measurementDate.toDateString() === today.toDateString();
        
        // ✅ STRICT: Only award points for TODAY'S measurements  
        // Check if BP is in healthy range (systolic 90-140, diastolic 60-90)
        const isHealthy = bp.systolic >= 90 && bp.systolic <= 140 && 
                         bp.diastolic >= 60 && bp.diastolic <= 90;
        
        // ❌ SECURITY FIX: Only award points for TODAY'S data, not recent data
        const canAwardPoints = isToday && isHealthy;
        
        if (!isToday) {
          console.log('🔍 BP DEBUG: Measurement not from today, no points awarded');
        }
        
        return {
          connected: true,
          systolic: bp.systolic,
          diastolic: bp.diastolic,
          timestamp: timestamp,
          deviceSource: deviceSource,
          meetsGoal: canAwardPoints, // Only TODAY + healthy range
          displayValue: isToday ? 
            `${bp.systolic}/${bp.diastolic} mmHg from ${deviceSource} ${isHealthy ? '✅' : '⚠️'}` : 
            `Last: ${bp.systolic}/${bp.diastolic} mmHg from ${deviceSource} (${new Date(timestamp).toLocaleDateString()}) - Not today`
        };
      }
      
      console.log('❌ BP DEBUG: No valid BP data found in:', latestMeasurement);
    } catch (error) {
      console.error('Error processing BP data:', error);
    }
    
    return { connected: false, displayValue: 'BP data processing error', meetsGoal: false, deviceSource: 'Unknown' };
  }, []);

  const processAppleWatchActivity = useCallback((activityDataResult) => {
    try {
      console.log('🔍 ACTIVITY DEBUG: Processing activity data result:', activityDataResult);
      
      if (activityDataResult.status !== 'fulfilled' || !activityDataResult.value?.success) {
        console.log('🔍 ACTIVITY DEBUG: API call failed or unsuccessful:', activityDataResult);
        return { connected: false, displayValue: 'Fitness tracker not connected', meetsGoal: false, deviceSource: 'Unknown' };
      }
      
      const data = activityDataResult.value.data;
      console.log('🔍 ACTIVITY DEBUG: API returned data array:', data);
      console.log('🔍 ACTIVITY DEBUG: Data length:', data ? data.length : 0);
      
      if (!data || !Array.isArray(data) || data.length === 0) {
        console.log('🔍 ACTIVITY DEBUG: No data array found or empty array');
        return { connected: true, displayValue: 'No recent activity data', meetsGoal: false, deviceSource: 'Unknown' };
      }
      
      // Get latest activity data and detect device source
      const latestActivity = data[0];
      console.log('🔍 ACTIVITY DEBUG: Raw activity data:', latestActivity);
      console.log('🔍 ACTIVITY DEBUG: Full data object keys:', Object.keys(latestActivity));
      console.log('🔍 ACTIVITY DEBUG: healthMetrics content:', latestActivity.healthMetrics);
      console.log('🔍 ACTIVITY DEBUG: data content:', latestActivity.data);
      
      let activity = null;
      let deviceSource = 'Unknown Fitness Tracker';
      
      // � DEVICE DETECTION: Extract device info from metadata
      const deviceInfo = latestActivity.data?.metadata?.deviceInfo;
      if (deviceInfo) {
        deviceSource = `${deviceInfo.deviceBrand} ${deviceInfo.deviceModel}` || deviceInfo.platform || 'Fitness Tracker';
        console.log('🔍 ACTIVITY DEBUG: Device detected:', deviceSource);
      }
      
      // 🔧 COMPREHENSIVE ACTIVITY DATA SEARCH: Look for activity data in all possible locations
      const searchLocations = [
        // Apple Watch structure (primary)
        latestActivity.healthMetrics?.activity,
        // Oura Ring structure
        latestActivity.healthMetrics?.activity,
        // Alternative structures
        latestActivity.healthMetrics?.fitness,
        latestActivity.data?.healthMetrics?.activity,
        latestActivity.data?.fitness,
        latestActivity.data?.activityData,
        latestActivity.fitness,
        latestActivity.metrics?.activity,
        latestActivity.activity,
        latestActivity.steps ? { steps: latestActivity.steps } : null
      ];
      
      // Find the first valid activity measurement
      for (const location of searchLocations) {
        if (location && (location.steps || location.calories || location.distance)) {
          activity = location;
          console.log('🔍 ACTIVITY DEBUG: Found activity data:', activity);
          break;
        }
      }
      
      // ❌ NO DEMO DATA: Only use real device measurements
      // If no real activity data found, return disconnected status
      if (!activity) {
        console.log('🔍 ACTIVITY DEBUG: No real activity data found - NO DEMO FALLBACK');
        return { 
          connected: false, 
          displayValue: 'No activity data available today', 
          meetsGoal: false, 
          deviceSource: deviceSource || 'Unknown' 
        };
      }
      
      if (activity && activity.steps) {
        const timestamp = latestActivity.timestamp || new Date().toISOString();
        const measurementDate = new Date(timestamp);
        const today = new Date();
        const isToday = measurementDate.toDateString() === today.toDateString();
        const meetsStepGoal = activity.steps >= 3000; // Daily step goal - Updated to 3000
        
        if (!isToday) {
          console.log('🔍 ACTIVITY DEBUG: Measurement not from today, no points awarded');
        }
        
        return {
          connected: true,
          steps: activity.steps,
          timestamp: timestamp,
          deviceSource: deviceSource,
          meetsGoal: isToday && meetsStepGoal, // Only TODAY + meets goal
          displayValue: isToday ? 
            `${activity.steps.toLocaleString()} steps from ${deviceSource} ${meetsStepGoal ? '✅' : '📈'}` : 
            `Last: ${activity.steps.toLocaleString()} steps from ${deviceSource} (${measurementDate.toLocaleDateString()}) - Not today`
        };
      }
    } catch (error) {
      console.error('Error processing activity data:', error);
    }
    
    return { connected: false, displayValue: 'Activity data processing error', meetsGoal: false, deviceSource: 'Unknown' };
  }, []);

  const processAppleWatchSleep = useCallback((sleepDataResult) => {
    try {
      if (sleepDataResult.status !== 'fulfilled' || !sleepDataResult.value?.success) {
        return { connected: false, displayValue: 'Sleep tracker not available', meetsGoal: false, deviceSource: 'Unknown' };
      }
      
      const data = sleepDataResult.value.data;
      if (!data || !Array.isArray(data) || data.length === 0) {
        return { connected: true, displayValue: 'No recent sleep data', meetsGoal: false, deviceSource: 'Unknown' };
      }
      
      // Get latest sleep data and detect device source
      const latestSleep = data[0];
      let sleep = null;
      let deviceSource = 'Unknown Sleep Tracker';
      
      // 🔧 MULTI-FORMAT: Handle different sleep tracker formats
      if (latestSleep.healthMetrics?.sleep) {
        sleep = latestSleep.healthMetrics.sleep;
        deviceSource = 'Apple Watch';
      } else if (latestSleep.sleepMetrics?.totalSleep) {
        sleep = { hours: latestSleep.sleepMetrics.totalSleep };
        deviceSource = 'Oura Ring';
      } else if (latestSleep.metrics?.sleep) {
        sleep = latestSleep.metrics.sleep;
        deviceSource = 'Smart Watch';
      } else if (latestSleep.sleep) {
        sleep = latestSleep.sleep;
        deviceSource = 'Sleep Tracker';
      }
      
      if (sleep && sleep.hours) {
        const timestamp = latestSleep.timestamp || new Date().toISOString();
        const lastNight = new Date(timestamp);
        const today = new Date();
        const isLastNight = lastNight.toDateString() === today.toDateString() || 
                           (today.getHours() < 12 && Math.abs(today - lastNight) < 24 * 60 * 60 * 1000);
        
        const hours = parseFloat(sleep.hours);
        const meetsGoal = hours >= 6; // 6+ hours goal - Updated to match validation rules
        
        return {
          connected: true,
          hours: hours,
          timestamp: timestamp,
          deviceSource: deviceSource,
          meetsGoal: isLastNight && meetsGoal,
          displayValue: isLastNight ? 
            `${hours.toFixed(1)} hours from ${deviceSource} ${meetsGoal ? '😴' : '😴⚠️'}` : 
            `Last: ${hours.toFixed(1)} hours from ${deviceSource} (${new Date(timestamp).toLocaleDateString()})`
        };
      }
    } catch (error) {
      console.error('Error processing sleep data:', error);
    }
    
    return { connected: false, displayValue: 'Sleep data processing error', meetsGoal: false, deviceSource: 'Unknown' };
  }, []);

  const processAppleWatchHRV = useCallback((hrvDataResult) => {
    try {
      if (hrvDataResult.status !== 'fulfilled' || !hrvDataResult.value?.success) {
        return { connected: false, displayValue: 'HRV tracker not available', meetsGoal: false, deviceSource: 'Unknown' };
      }
      
      const data = hrvDataResult.value.data;
      if (!data || !Array.isArray(data) || data.length === 0) {
        return { connected: true, displayValue: 'No recent HRV data', meetsGoal: false, deviceSource: 'Unknown' };
      }
      
      // Get latest HRV data and detect device source
      const latestHRV = data[0];
      let hrv = null;
      let deviceSource = 'Unknown HRV Tracker';
      
      // 🔧 MULTI-FORMAT: Handle different HRV tracker formats
      if (latestHRV.healthMetrics?.hrv) {
        hrv = latestHRV.healthMetrics.hrv;
        deviceSource = 'Apple Watch';
      } else if (latestHRV.cardiovascular?.heartRateVariability) {
        hrv = { value: latestHRV.cardiovascular.heartRateVariability };
        deviceSource = 'Withings Body Scan';
      } else if (latestHRV.metrics?.hrv) {
        hrv = latestHRV.metrics.hrv;
        deviceSource = 'Heart Rate Monitor';
      } else if (latestHRV.hrv) {
        hrv = latestHRV.hrv;
        deviceSource = 'HRV Monitor';
      }
      
      if (hrv && hrv.value) {
        const timestamp = latestHRV.timestamp || new Date().toISOString();
        const isToday = new Date(timestamp).toDateString() === new Date().toDateString();
        const value = parseFloat(hrv.value);
        const meetsGoal = value >= 20; // 20ms+ HRV goal - Updated to match validation rules
        
        return {
          connected: true,
          value: value,
          timestamp: timestamp,
          deviceSource: deviceSource,
          meetsGoal: isToday && meetsGoal,
          displayValue: isToday ? 
            `${Math.round(value)} ms from ${deviceSource} ${meetsGoal ? '💚' : '💛'}` : 
            `Last: ${Math.round(value)} ms from ${deviceSource} (${new Date(timestamp).toLocaleDateString()})`
        };
      }
    } catch (error) {
      console.error('Error processing HRV data:', error);
    }
    
    return { connected: false, displayValue: 'HRV data processing error', meetsGoal: false, deviceSource: 'Unknown' };
  }, []);

  //  VALIDATION: Ensure device data meets activity requirements
  const validateDeviceDataForActivity = useCallback((activity, deviceData) => {
    if (!deviceData || !activity.validationRules) return false;
    
    switch (activity.dataType) {
      case 'weight':
        return deviceData.value >= activity.validationRules.minValue && 
               deviceData.value <= activity.validationRules.maxValue;
               
      case 'blood_pressure':
        return deviceData.systolic >= activity.validationRules.systolicRange[0] && 
               deviceData.systolic <= activity.validationRules.systolicRange[1] &&
               deviceData.diastolic >= activity.validationRules.diastolicRange[0] && 
               deviceData.diastolic <= activity.validationRules.diastolicRange[1];
               
      case 'activity':
        return deviceData.steps >= activity.validationRules.minSteps;
        
      case 'sleep':
        return deviceData.hours >= activity.validationRules.minHours && 
               deviceData.hours <= activity.validationRules.maxHours;
               
      case 'hrv':
        return deviceData.value >= activity.validationRules.minHRV && 
               deviceData.value <= activity.validationRules.maxHRV;
               
      default:
        return false;
    }
  }, []);

  // 🎉 AUTOMATIC POINT AWARDING: Award points when IoMT data meets goals
  const autoAwardPointsForActivity = useCallback(async (activityId, deviceData) => {
    const activity = dailyActivities.find(a => a.id === activityId);
    if (!activity || activity.claimedToday) return;
    
    // Check local storage to see if we already awarded points today for this activity
    const today = new Date().toDateString();
    const storedClaims = JSON.parse(localStorage.getItem(`dailyClaims_${userId}_${today}`) || '{}');
    if (storedClaims[activityId]) {
      console.log(`🚫 AUTO-AWARD: Already awarded points today for ${activity.name}`);
      return;
    }
    
    try {
      console.log(`🤖 AUTO-AWARD: Processing automatic points for ${activity.name}...`);
      
      // Validate device data meets activity requirements
      if (!validateDeviceDataForActivity(activity, deviceData)) {
        console.log(`❌ AUTO-AWARD: Device data doesn't meet requirements for ${activity.name}`);
        return;
      }
      
      // Additional check: verify backend hasn't already awarded points today for this activity
      try {
        const dailyClaimsResult = await medipointsApiService.getDailyClaimedActivities(userId, userCountry);
        if (dailyClaimsResult.success) {
          const todaysClaims = dailyClaimsResult.data.claimedActivities;
          if (todaysClaims.includes(activityId)) {
            console.log(`🚫 AUTO-AWARD: Backend shows ${activity.name} already claimed today`);
            // Update local state to reflect backend state
            setDailyActivities(prev => prev.map(a => 
              a.id === activityId ? { ...a, claimedToday: true } : a
            ));
            storedClaims[activityId] = true;
            localStorage.setItem(`dailyClaims_${userId}_${today}`, JSON.stringify(storedClaims));
            return;
          }
        }
      } catch (checkError) {
        console.warn('⚠️ AUTO-AWARD: Could not verify backend claims, proceeding with caution');
      }
      
      // Award points using the existing system
      const metadata = {
        activityType: activityId,
        activityId: activityId,
        activityName: activity.name,
        dataType: activity.dataType,
        deviceConnected: true,
        deviceValidated: true,
        automaticAward: true, // 🚀 NEW: Mark as automatic award
        deviceData: deviceData
      };
      
      const savedSuccessfully = await saveMediPointsToBackend(
        activity.points,
        activityId,
        metadata
      );
      
      if (savedSuccessfully) {
        // Update activity state
        setDailyActivities(prev => prev.map(a => 
          a.id === activityId ? { ...a, claimedToday: true } : a
        ));
        
        // Store claim locally
        storedClaims[activityId] = true;
        localStorage.setItem(`dailyClaims_${userId}_${today}`, JSON.stringify(storedClaims));
        
        // Update today's points counter
        setTotalPointsEarnedToday(prev => prev + activity.points);
        
        // Show success notification
        showNotification(`🤖 AUTO-AWARDED: +${activity.points} MediPoints for ${activity.name}! Device data: ${deviceData.displayValue}`);
        
        console.log(`✅ AUTO-AWARD: Successfully awarded ${activity.points} points for ${activity.name}`);
      }
      
    } catch (error) {
      console.error('❌ AUTO-AWARD: Error awarding automatic points:', error);
    }
  }, [dailyActivities, userId, userCountry, saveMediPointsToBackend, showNotification, validateDeviceDataForActivity]);

  // 🚀 SIMPLIFIED DEVICE DATA CHECK: Use the working healthData from LandingPage
  const checkDeviceDataAndAwardPoints = useCallback(async () => {
    if (!userId || userId === 'temp_user_id' || !healthData) return;
    
    try {
      console.log('🔍 DEVICE CHECK: Using real health data for point awarding...');
      console.log('� DEVICE DATA:', { 
        devices: healthData.devices?.length || 0,
        activity: healthData.activity,
        vitals: healthData.vitals,
        wellness: healthData.wellness
      });
      
      // Process each dynamic activity for automatic point awarding using real health data
      setDailyActivities(prev => prev.map(activity => {
        if (!activity.isDynamic || activity.isComingSoon || activity.claimedToday) return activity;
        
        console.log(`📱 CHECKING: ${activity.name} (${activity.dataType})`);
        
        let hasValidData = false;
        let meetsGoal = false;
        let displayValue = 'Waiting for device sync...';
        let shouldAutoAward = false;
        
        // Check each activity type against real health data - USING CORRECT DATA PATHS
        switch (activity.dataType) {
          case 'weight':
            // Weight data - check MULTIPLE paths where Azure Function might store weight data
            let weightFound = false;
            let weightValue = 0;
            
            // Path 1: Azure Function fusedData.fusedMetrics.weight (primary from withings-body-scan)
            if (healthData._raw?.fusedData?.fusedMetrics?.weight) {
              hasValidData = true;
              weightValue = parseFloat(healthData._raw.fusedData.fusedMetrics.weight.value || healthData._raw.fusedData.fusedMetrics.weight) || 0;
              weightFound = true;
              console.log(`✅ WEIGHT (fusedData): ${weightValue} kg from ${healthData._raw.fusedData.fusedMetrics.weight.sourceDevice || 'Azure Function'}`);
            }
            // Path 2: Raw response data from Azure Function
            else if (healthData._raw?.originalResponse?.fusedData?.fusedMetrics?.weight) {
              hasValidData = true;
              weightValue = parseFloat(healthData._raw.originalResponse.fusedData.fusedMetrics.weight.value || healthData._raw.originalResponse.fusedData.fusedMetrics.weight) || 0;
              weightFound = true;
              console.log(`✅ WEIGHT (originalResponse): ${weightValue} kg from ${healthData._raw.originalResponse.fusedData.fusedMetrics.weight.sourceDevice || 'Azure Function'}`);
            }
            // Path 3: realTimeMetrics.bodyComposition (processed data)
            else if (healthData.realTimeMetrics?.bodyComposition?.weight || healthData.wellness?.weight) {
              hasValidData = true;
              weightValue = parseFloat(healthData.realTimeMetrics?.bodyComposition?.weight || healthData.wellness?.weight) || 0;
              weightFound = true;
              console.log(`✅ WEIGHT (realTimeMetrics): ${weightValue} kg`);
            }
            // Path 4: vitals.bodyComposition (fallback)
            else if (healthData.vitals?.bodyComposition?.weight) {
              hasValidData = true;
              weightValue = parseFloat(healthData.vitals.bodyComposition.weight) || 0;
              weightFound = true;
              console.log(`✅ WEIGHT (vitals): ${weightValue} kg`);
            }
            // Path 5: Check raw device data directly
            else if (healthData._raw?.deviceData) {
              const withingsData = healthData._raw.deviceData['withings-body-scan'] || healthData._raw.deviceData['withings'];
              if (withingsData && withingsData.length > 0) {
                const latestWithings = withingsData[0];
                if (latestWithings.healthMetrics?.bodyComposition?.weight) {
                  hasValidData = true;
                  weightValue = parseFloat(latestWithings.healthMetrics.bodyComposition.weight) || 0;
                  weightFound = true;
                  console.log(`✅ WEIGHT (raw withings): ${weightValue} kg`);
                }
              }
            }
            
            if (weightFound) {
              displayValue = `${weightValue.toFixed(1)} kg`;
              meetsGoal = weightValue > 30 && weightValue < 300; // Reasonable weight range
              shouldAutoAward = meetsGoal;
              console.log(`🏆 WEIGHT FINAL: ${displayValue}, meets goal: ${meetsGoal}`);
            } else {
              console.log(`❌ WEIGHT: No weight data found in any path`);
              console.log(`🔍 WEIGHT DEBUG: Available data paths:`, {
                fusedData: !!healthData._raw?.fusedData,
                originalResponse: !!healthData._raw?.originalResponse,
                realTimeMetrics: !!healthData.realTimeMetrics,
                deviceData: !!healthData._raw?.deviceData
              });
            }
            break;
            
          case 'blood_pressure':
            // Blood pressure data - check realTimeMetrics.bloodPressure like LandingPage does
            if (healthData.realTimeMetrics?.bloodPressure?.systolic && healthData.realTimeMetrics?.bloodPressure?.diastolic) {
              hasValidData = true;
              const systolic = parseInt(healthData.realTimeMetrics.bloodPressure.systolic) || 0;
              const diastolic = parseInt(healthData.realTimeMetrics.bloodPressure.diastolic) || 0;
              displayValue = `${systolic}/${diastolic} mmHg`;
              meetsGoal = true; // Any BP reading qualifies
              shouldAutoAward = meetsGoal;
              console.log(`✅ BLOOD PRESSURE: ${displayValue}, meets goal: ${meetsGoal}`);
            }
            // Fallback: Check vitals.bloodPressure
            else if (healthData.vitals?.bloodPressure?.systolic && healthData.vitals?.bloodPressure?.diastolic) {
              hasValidData = true;
              const systolic = parseInt(healthData.vitals.bloodPressure.systolic) || 0;
              const diastolic = parseInt(healthData.vitals.bloodPressure.diastolic) || 0;
              displayValue = `${systolic}/${diastolic} mmHg`;
              meetsGoal = true; // Any BP reading qualifies
              shouldAutoAward = meetsGoal;
              console.log(`✅ BLOOD PRESSURE (vitals): ${displayValue}, meets goal: ${meetsGoal}`);
            }
            break;
            
          case 'activity':
            // Activity data - use EXACT same paths as LandingPage (realTimeMetrics.steps)
            if (healthData.realTimeMetrics?.steps) {
              hasValidData = true;
              const steps = (healthData.realTimeMetrics.steps && typeof healthData.realTimeMetrics.steps === 'object') 
                ? (healthData.realTimeMetrics.steps.value || 0)
                : (healthData.realTimeMetrics.steps || 0);
              displayValue = `${steps.toLocaleString()} steps today`;
              meetsGoal = steps >= 3000; // 3000 step goal
              shouldAutoAward = meetsGoal;
              console.log(`✅ ACTIVITY: ${displayValue}, meets goal: ${meetsGoal} (3000+ required)`);
            }
            break;
            
          case 'sleep':
            // Sleep data - check realTimeMetrics.sleep like LandingPage does
            if (healthData.realTimeMetrics?.sleep) {
              hasValidData = true;
              let sleepValue = parseFloat(healthData.realTimeMetrics.sleep) || 0;
              
              // Convert minutes to hours if the value is suspiciously high (likely in minutes)
              if (sleepValue > 24) {
                sleepValue = sleepValue / 60; // Convert minutes to hours
                console.log(`🔧 SLEEP: Converted ${healthData.realTimeMetrics.sleep} minutes to ${sleepValue.toFixed(1)} hours`);
              }
              
              displayValue = `${sleepValue.toFixed(1)} hours last night`;
              meetsGoal = sleepValue >= 6; // 6+ hours goal
              shouldAutoAward = meetsGoal;
              console.log(`✅ SLEEP: ${displayValue}, meets goal: ${meetsGoal} (6+ hours required)`);
            }
            // Fallback: Check wellness.sleep
            else if (healthData.wellness?.sleep) {
              hasValidData = true;
              let sleepValue = parseFloat(healthData.wellness.sleep) || 0;
              
              // Convert minutes to hours if the value is suspiciously high (likely in minutes)
              if (sleepValue > 24) {
                sleepValue = sleepValue / 60; // Convert minutes to hours
                console.log(`🔧 SLEEP: Converted ${healthData.wellness.sleep} minutes to ${sleepValue.toFixed(1)} hours`);
              }
              
              displayValue = `${sleepValue.toFixed(1)} hours last night`;
              meetsGoal = sleepValue >= 6; // 6+ hours goal
              shouldAutoAward = meetsGoal;
              console.log(`✅ SLEEP (wellness): ${displayValue}, meets goal: ${meetsGoal} (6+ hours required)`);
            }
            break;
            
          case 'hydration':
            // Hydration data - check realTimeMetrics.water like LandingPage does
            if (healthData.realTimeMetrics?.water || healthData.realTimeMetrics?.waterML) {
              hasValidData = true;
              const waterGlasses = parseInt(healthData.realTimeMetrics.water) || 0;
              const waterMl = parseInt(healthData.realTimeMetrics.waterML) || (waterGlasses * 240);
              displayValue = `${waterGlasses} glasses today (${waterMl}ml)`;
              meetsGoal = waterGlasses >= 8; // 8+ glasses goal
              shouldAutoAward = meetsGoal;
              console.log(`✅ HYDRATION: ${displayValue}, meets goal: ${meetsGoal} (8+ glasses required)`);
            }
            // Fallback: Check wellness.hydration
            else if (healthData.wellness?.hydration?.glasses) {
              hasValidData = true;
              const waterGlasses = parseInt(healthData.wellness.hydration.glasses) || 0;
              const waterMl = parseInt(healthData.wellness.hydration.ml) || (waterGlasses * 240);
              displayValue = `${waterGlasses} glasses today (${waterMl}ml)`;
              meetsGoal = waterGlasses >= 8; // 8+ glasses goal
              shouldAutoAward = meetsGoal;
              console.log(`✅ HYDRATION (wellness): ${displayValue}, meets goal: ${meetsGoal} (8+ glasses required)`);
            }
            break;
            
          case 'hrv':
            // HRV data - check realTimeMetrics.heartRateVariability like LandingPage does
            if (healthData.realTimeMetrics?.heartRateVariability) {
              hasValidData = true;
              const hrv = parseFloat(healthData.realTimeMetrics.heartRateVariability) || 0;
              displayValue = `${hrv} ms RMSSD`;
              meetsGoal = true; // Any HRV reading qualifies
              shouldAutoAward = meetsGoal;
              console.log(`✅ HRV: ${displayValue}, meets goal: ${meetsGoal}`);
            }
            // Fallback: Check vitals.heartRate as proxy for autonomic function
            else if (healthData.vitals?.heartRate || healthData.realTimeMetrics?.heartRate) {
              hasValidData = true;
              const heartRate = parseInt(healthData.vitals?.heartRate || healthData.realTimeMetrics?.heartRate) || 0;
              displayValue = `${heartRate} bpm average`;
              meetsGoal = true; // Any HR reading qualifies for autonomic assessment
              shouldAutoAward = meetsGoal;
              console.log(`✅ HRV (HR proxy): ${displayValue}, meets goal: ${meetsGoal}`);
            }
            break;
            
          default:
            // No data available for this activity type
            break;
        }
        
        // Update activity status and mark for auto-award if criteria met
        const updatedActivity = {
          ...activity,
          deviceConnected: hasValidData,
          dataAvailable: hasValidData,
          currentValue: displayValue,
          lastDataTimestamp: hasValidData ? new Date().toISOString() : activity.lastDataTimestamp,
          shouldAutoAward: shouldAutoAward && !activity.claimedToday // Flag for auto-award
        };
        
        return updatedActivity;
      }));
      
    } catch (error) {
      console.error('❌ DEVICE CHECK: Error processing device data:', error);
    }
  }, [userId, healthData]); // Clean dependencies without autoAwardPointsForActivity

  // Separate effect to handle auto-awarding points when activities are marked for auto-award
  useEffect(() => {
    dailyActivities.forEach(activity => {
      if (activity.shouldAutoAward && !activity.claimedToday) {
        console.log(`🎉 AUTO-AWARD: ${activity.name} qualifies for ${activity.points} points!`);
        
        // Trigger automatic point awarding with a slight delay
        setTimeout(() => {
          autoAwardPointsForActivity(activity.id, {
            connected: true,
            meetsGoal: true,
            displayValue: activity.currentValue,
            timestamp: new Date().toISOString()
          });
        }, 500);
      }
    });
  }, [dailyActivities, autoAwardPointsForActivity]);

  // 🔄 DEVICE DATA MONITORING: Check for new device data when health data updates
  useEffect(() => {
    if (userId && userId !== 'temp_user_id' && healthData && !healthDataLoading) {
      console.log('🔄 HEALTH DATA: New data available, checking for point awarding...');
      checkDeviceDataAndAwardPoints();
    }
  }, [userId, healthData, healthDataLoading, checkDeviceDataAndAwardPoints]);

  // 🚀 MODERNIZED: Dynamic device connection check (no more manual entry)
  const checkDeviceConnections = useCallback(async () => {
    try {
      console.log('🔍 ANTI-GAMING: Checking real device connections...');
      
      // All activities now require device connections - no manual entry allowed
      setDailyActivities(prev => prev.map(activity => {
        if (activity.isComingSoon) {
          return {
            ...activity,
            deviceConnected: false,
            dataAvailable: false,
            currentValue: 'Feature coming in Phase 02...'
          };
        }
        
        // For dynamic activities, device status is updated via IoMT monitoring
        return activity;
      }));
      
    } catch (error) {
      console.error('❌ Error checking device connections:', error);
    }
  }, []);

  // Production Claim Validation and Processing (Anti-Gaming System)
  const claimDailyActivity = useCallback(async (activityId) => {
    if (!userId) {
      showNotification('❌ Authentication required to claim points');
      return;
    }
    
    const activity = dailyActivities.find(a => a.id === activityId);
    if (!activity) {
      showNotification('❌ Activity not found');
      return;
    }
    
    // Check if already claimed today
    if (activity.claimedToday) {
      showNotification('⚠️ Activity already claimed today. Available again at midnight!');
      return;
    }

    // 🔒 PRODUCTION DAILY CLAIM VALIDATION: Check backend for today's claims
    setClaimingStates(prev => ({ ...prev, [activityId]: 'validating' }));
    
    try {
      console.log(`🔍 Checking daily claim validation for: ${activity.name}`);
      
      // Use imported service to check daily claims
      const dailyClaimsResult = await medipointsApiService.getDailyClaimedActivities(userId, userCountry);
      
      if (dailyClaimsResult.success) {
        const todaysClaims = dailyClaimsResult.data.claimedActivities;
        const alreadyClaimed = todaysClaims.includes(activityId);
        
        if (alreadyClaimed) {
          showNotification('⚠️ This activity has already been claimed today! Next claim available at midnight.');
          setClaimingStates(prev => ({ ...prev, [activityId]: 'error' }));
          setTimeout(() => {
            setClaimingStates(prev => ({ ...prev, [activityId]: null }));
          }, 3000);
          return;
        }
        
        console.log(`✅ Daily claim validation passed. Today's claims: ${todaysClaims.length}`);
      } else {
        console.warn('⚠️ Could not validate daily claims, proceeding with caution:', dailyClaimsResult.error);
      }
    } catch (error) {
      console.error('❌ Error validating daily claims:', error);
      showNotification('⚠️ Validation error - please try again');
      setClaimingStates(prev => ({ ...prev, [activityId]: null }));
      return;
    }

    // 🚀 MODERNIZED: Check for Coming Soon features
    if (activity.isComingSoon) {
      showNotification('🚧 This feature is coming soon in Phase 02! Stay tuned for advanced nutrition tracking.');
      setClaimingStates(prev => ({ ...prev, [activityId]: null }));
      return;
    }

    // ANTI-GAMING VALIDATION: Check device requirements
    if (activity.validationRules.deviceRequired && !activity.deviceConnected) {
      showNotification(`⚠️ ${activity.name} requires a connected ${activity.dataSources[0]} device. Please connect and sync your device data.`);
      setClaimingStates(prev => ({ ...prev, [activityId]: null }));
      return;
    }

    // Check if data is available for claim
    if (!activity.dataAvailable) {
      showNotification('⚠️ No valid device data detected. Please ensure your device is connected and has synced recent data.');
      setClaimingStates(prev => ({ ...prev, [activityId]: null }));
      return;
    }
    
    setClaimingStates(prev => ({ ...prev, [activityId]: 'claiming' }));
    
    try {
      // PRODUCTION: Record claim to backend MediCureOn Data with device validation
      const claimData = {
        userId,
        activityId,
        activityName: activity.name,
        points: activity.points,
        timestamp: new Date().toISOString(),
        dataType: activity.dataType,
        deviceConnected: activity.deviceConnected,
        deviceRequired: activity.validationRules.deviceRequired,
        dataValue: activity.currentValue,
        country: userCountry,
        // Anti-gaming metadata
        deviceValidated: activity.validationRules.deviceRequired ? activity.deviceConnected : true,
        manualEntry: activity.validationRules.deviceRequired === false,
        lastDataTimestamp: activity.lastDataTimestamp
      };
      
      // Save MediPoints to MediCureOn Data using medi-points-manager
      const savedSuccessfully = await saveMediPointsToBackend(
        activity.points, 
        activityId, // 🔧 CRITICAL FIX: Pass activity ID for backend validation
        {
          activityType: activityId, // 🔧 BACKEND COMPATIBILITY: This is what getTodaysClaimedActivities expects
          activityId: activityId,
          activityName: activity.name,
          dataType: activity.dataType,
          deviceConnected: activity.deviceConnected,
          deviceValidated: claimData.deviceValidated,
          manualEntry: claimData.manualEntry
        }
      );
      
      if (savedSuccessfully) {
        console.log('✅ PRODUCTION: MediPoints successfully saved to MediCureOn Data');
      } else {
        console.warn('⚠️ MediPoints saved locally, Azure sync will retry');
      }
      
      // TODO: Optional - Also save activity claim details to separate function
      // This would track individual activity completions vs just points
      // await saveActivityClaimToBackend(claimData);
      
      // Update local state for UI feedback
      setDailyActivities(prev => prev.map(a => 
        a.id === activityId ? { ...a, claimedToday: true } : a
      ));
      
      // Store claim locally for midnight reset functionality
      const today = new Date().toDateString();
      const storedClaims = JSON.parse(localStorage.getItem(`dailyClaims_${userId}_${today}`) || '{}');
      storedClaims[activityId] = true;
      localStorage.setItem(`dailyClaims_${userId}_${today}`, JSON.stringify(storedClaims));
      
      // Update today's points counter (backend handles total points)
      setTotalPointsEarnedToday(prev => prev + activity.points);
      
      setClaimingStates(prev => ({ ...prev, [activityId]: 'success' }));
      showNotification(`🎉 +${activity.points} MediPoints earned from ${activity.name}! New Balance: ${(totalMediPoints + activity.points).toLocaleString()}`);
      
      setTimeout(() => {
        setClaimingStates(prev => ({ ...prev, [activityId]: null }));
      }, 3000);
      
    } catch (error) {
      console.error('❌ Error claiming activity:', error);
      setClaimingStates(prev => ({ ...prev, [activityId]: 'error' }));
      showNotification('❌ Failed to claim points. Please try again.');
      
      setTimeout(() => {
        setClaimingStates(prev => ({ ...prev, [activityId]: null }));
      }, 3000);
    }
  }, [userId, dailyActivities, showNotification, userCountry, saveMediPointsToBackend, totalMediPoints]);

  // Load today's claimed activities from localStorage and backend
  const loadTodaysClaimedActivities = useCallback(async () => {
    if (!userId || userId === 'temp_user_id') return;
    
    try {
      const today = new Date().toDateString();
      
      // Load from localStorage first (faster)
      const storedClaims = JSON.parse(localStorage.getItem(`dailyClaims_${userId}_${today}`) || '{}');
      
      // Update activities with local storage claims
      setDailyActivities(prev => prev.map(activity => ({
        ...activity,
        claimedToday: storedClaims[activity.id] || false
      })));
      
      // Also verify with backend for consistency
      try {
        const dailyClaimsResult = await medipointsApiService.getDailyClaimedActivities(userId, userCountry);
        if (dailyClaimsResult.success) {
          const backendClaims = dailyClaimsResult.data.claimedActivities || [];
          
          // Merge backend claims with local storage
          const mergedClaims = { ...storedClaims };
          backendClaims.forEach(activityId => {
            mergedClaims[activityId] = true;
          });
          
          // Update localStorage with merged data
          localStorage.setItem(`dailyClaims_${userId}_${today}`, JSON.stringify(mergedClaims));
          
          // Update UI with complete claim status
          setDailyActivities(prev => prev.map(activity => ({
            ...activity,
            claimedToday: mergedClaims[activity.id] || false
          })));
          
          console.log(`✅ Loaded today's claimed activities: ${backendClaims.length} from backend, ${Object.keys(storedClaims).length} from local storage`);
        }
      } catch (backendError) {
        console.warn('⚠️ Could not verify claims with backend, using local storage only');
      }
      
    } catch (error) {
      console.error('❌ Error loading today\'s claimed activities:', error);
    }
  }, [userId, userCountry]);

  // Load MediPoints and daily claims on component mount
  useEffect(() => {
    if (userId && userId !== 'temp_user_id') {
      loadUserMediPoints();
      loadTodaysClaimedActivities();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]); // Only depend on userId to avoid recreating function

  // Check device connections on component mount and every 2 minutes
  useEffect(() => {
    checkDeviceConnections();
    const interval = setInterval(checkDeviceConnections, 120000); // Check every 2 minutes
    
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount to avoid recreating interval

  // 🌟 PRODUCTION BONUS OPPORTUNITIES - MediCureOn Data File Detection
  const [bonusOpportunities, setBonusOpportunities] = useState({
    profileComplete: {
      claimed: false,
      completionPercentage: 0,
      isLoading: true,
      filesFound: {
        generalInfo: false,
        healthMedical: false,
        medicationsLifestyle: false
      }
    },
    firstPlan: {
      claimed: false,
      isLoading: true,
      planExists: false
    },
    referral: {
      totalReferred: 0,
      comingSoon: true // Phase 2 feature
    }
  });

  // 🎁 AWARD BONUS POINTS - Unified bonus awarding system with duplicate prevention
  const awardBonusPoints = useCallback(async (bonusType, points, description, metadata = {}) => {
    // 🔒 CRITICAL: Prevent duplicate awards with timing checks
    const awardKey = `bonus_${bonusType}_${userId}`;
    const lastAwardTime = sessionStorage.getItem(awardKey);
    const now = Date.now();
    
    // Prevent duplicate awards within 30 seconds
    if (lastAwardTime && (now - parseInt(lastAwardTime)) < 30000) {
      console.log(`🚫 BLOCKED: Duplicate bonus award attempt for ${bonusType} (last awarded ${Math.round((now - parseInt(lastAwardTime)) / 1000)}s ago)`);
      return false;
    }
    
    // Set timestamp to prevent duplicates
    sessionStorage.setItem(awardKey, now.toString());
    
    try {
      console.log(`🎁 Awarding ${points} bonus points for: ${description}`);
      
      // Call MediPoints API directly with proper source for one-time bonuses
      const result = await medipointsApiService.addPoints(
        userId,
        points,
        'One-Time Bonus', // This matches what checkBonusEligibility looks for
        description,
        {
          bonusType: bonusType,
          description: description,
          source: 'AutomaticBonusSystem',
          oneTimeBonus: true,
          ...metadata
        },
        userCountry,
        await getAccessToken?.()
      );

      if (result.success) {
        console.log('✅ Bonus points awarded successfully:', result.data);
        
        // Update connection status since API call succeeded
        setIsBackendConnected(true);
        
        // Update UI state
        setBonusOpportunities(prev => ({
          ...prev,
          [bonusType === 'profile_completion' ? 'profileComplete' : 'firstPlan']: {
            ...prev[bonusType === 'profile_completion' ? 'profileComplete' : 'firstPlan'],
            claimed: true,
            readyToAward: false
          }
        }));

        // Show success notification
        showNotification(`🎉 Bonus Awarded! +${points} MediPoints for ${description}! New Balance: ${(totalMediPoints + points).toLocaleString()}`);
        
        return true;
      } else {
        console.error('❌ Failed to award bonus points:', result.error);
        // Remove the prevention timestamp on failure so it can be retried
        sessionStorage.removeItem(awardKey);
        return false;
      }
    } catch (error) {
      console.error('❌ Error awarding bonus points:', error);
      // Remove the prevention timestamp on error so it can be retried
      sessionStorage.removeItem(awardKey);
      return false;
    }
  }, [userId, userCountry, getAccessToken, showNotification, totalMediPoints]);

  // 🔍 MEDICUREON DATA FILE DETECTION - Check for profile completion
  const checkProfileCompletion = useCallback(async () => {
    if (!userId || userId === 'temp_user_id') return;

    try {
      console.log('🔍 Checking profile completion via backend bonus eligibility...');
      
      // Check backend for file existence and bonus eligibility
      const bonusCheckResult = await medipointsApiService.checkBonusEligibility(userId, 'profile_completion', userCountry);
      
      if (bonusCheckResult.success) {
        const { alreadyClaimed, fileDetails } = bonusCheckResult.data;
        
        // Update connection status since API call succeeded
        setIsBackendConnected(true);
        
        // Extract file existence from backend response
        const completedFiles = fileDetails.existingFiles || 0;
        const totalFiles = fileDetails.totalFiles || 3;
        const completionPercentage = Math.round((completedFiles / totalFiles) * 100);
        const isFullyComplete = fileDetails.allFilesExist || false;

        console.log(`📊 Profile completion: ${completedFiles}/${totalFiles} files (${completionPercentage}%)`, fileDetails);

        setBonusOpportunities(prev => ({
          ...prev,
          profileComplete: {
            claimed: alreadyClaimed,
            completionPercentage: completionPercentage,
            isLoading: false,
            filesFound: fileDetails.files || {},
            readyToAward: isFullyComplete && !alreadyClaimed
          }
        }));

        // 🎉 AUTO-AWARD 500 POINTS if profile is complete and not yet claimed
        if (isFullyComplete && !alreadyClaimed) {
          console.log('🎉 Profile completion detected! Auto-awarding 500 bonus points...');
          await awardBonusPoints('profile_completion', 500, 'Profile Completion Bonus', {
            filesCompleted: completedFiles,
            completionPercentage: 100,
            autoAwarded: true
          });
        }
      } else {
        console.error('❌ Failed to check profile completion:', bonusCheckResult.error);
        setBonusOpportunities(prev => ({
          ...prev,
          profileComplete: { ...prev.profileComplete, isLoading: false }
        }));
      }

    } catch (error) {
      console.error('❌ Error checking profile completion:', error);
      setBonusOpportunities(prev => ({
        ...prev,
        profileComplete: { ...prev.profileComplete, isLoading: false }
      }));
    }
  }, [userId, userCountry, awardBonusPoints]);

  // 🔍 MEDICUREON DATA FILE DETECTION - Check for healthcare plan enrollment
  const checkHealthcarePlanEnrollment = useCallback(async () => {
    if (!userId || userId === 'temp_user_id') return;

    try {
      console.log('🔍 Checking healthcare plan enrollment via backend bonus eligibility...');
      
      // Check backend for file existence and bonus eligibility
      const bonusCheckResult = await medipointsApiService.checkBonusEligibility(userId, 'healthcare_plan', userCountry);
      
      if (bonusCheckResult.success) {
        const { alreadyClaimed, fileDetails } = bonusCheckResult.data;
        
        // Update connection status since API call succeeded
        setIsBackendConnected(true);
        
        const planExists = fileDetails.allFilesExist || false;

        console.log(`🏥 Healthcare plan status: ${planExists ? 'Enrolled' : 'Not enrolled'}`, fileDetails);

        setBonusOpportunities(prev => ({
          ...prev,
          firstPlan: {
            claimed: alreadyClaimed,
            isLoading: false,
            planExists: planExists,
            readyToAward: planExists && !alreadyClaimed
          }
        }));

        // 🎉 AUTO-AWARD 300 POINTS if plan exists and not yet claimed
        if (planExists && !alreadyClaimed) {
          console.log('🎉 Healthcare plan enrollment detected! Auto-awarding 300 bonus points...');
          await awardBonusPoints('healthcare_plan', 300, 'First Healthcare Plan Bonus', {
            planEnrollment: true,
            autoAwarded: true
          });
        }
      } else {
        console.error('❌ Failed to check healthcare plan enrollment:', bonusCheckResult.error);
        setBonusOpportunities(prev => ({
          ...prev,
          firstPlan: { ...prev.firstPlan, isLoading: false }
        }));
      }

    } catch (error) {
      console.error('❌ Error checking healthcare plan enrollment:', error);
      setBonusOpportunities(prev => ({
        ...prev,
        firstPlan: { ...prev.firstPlan, isLoading: false }
      }));
    }
  }, [userId, userCountry, awardBonusPoints]);

  // 🎁 BONUS OPPORTUNITIES: Check profile completion and healthcare plan enrollment
  useEffect(() => {
    if (userId && userId !== 'temp_user_id') {
      console.log('🎁 Initializing bonus opportunities system...');
      
      // Check profile completion
      checkProfileCompletion();
      
      // Check healthcare plan enrollment
      checkHealthcarePlanEnrollment();
      
      // Set up periodic checks every 5 minutes for file changes
      const bonusCheckInterval = setInterval(() => {
        checkProfileCompletion();
        checkHealthcarePlanEnrollment();
      }, 300000); // 5 minutes
      
      return () => clearInterval(bonusCheckInterval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]); // Only depend on userId to avoid recreating functions and intervals

  return (
    <div className="space-y-6">
      {/* Professional Daily Health Activities */}
      <div className="p-8 bg-white border border-gray-100 shadow-lg rounded-2xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center mb-2 space-x-3">
              <div className="p-2 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Daily Health Activities</h3>
              <div className="px-3 py-1 text-sm font-medium text-purple-700 bg-purple-100 rounded-full">
                6 Activities
              </div>
            </div>
            <p className="max-w-2xl leading-relaxed text-gray-600">
              Complete health activities automatically detected by your connected IoMT devices. Each activity awards <strong>10 MediPoints</strong> once daily when device data meets health goals. Claims reset at midnight.
              <br />
              <span className="text-sm font-medium text-blue-600">🤖 All data is automatically detected from connected devices - no manual entry required for a true healthcare platform experience.</span>
            </p>
          </div>
          <div className="text-right">
            <p className="mb-1 text-sm text-gray-500">Your Total Balance</p>
            <div className="flex items-center mb-3 space-x-2">
              <Crown className="w-6 h-6 text-yellow-500" />
              <p className="text-4xl font-bold text-transparent bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text">
                {totalMediPoints.toLocaleString()}
              </p>
              <span className="text-lg font-medium text-gray-700">pts</span>
            </div>
            <div className="space-y-1 text-sm text-gray-600">
              <p>💰 Available Today: {dailyActivities.filter(activity => !activity.claimedToday).reduce((sum, activity) => sum + activity.points, 0)} pts</p>
              <p>🏆 From {dailyActivities.filter(activity => activity.claimedToday).length} activities</p>
              <p className={`text-xs ${isBackendConnected ? 'text-green-600' : 'text-orange-600'}`}>
                {isBackendConnected ? '☁️ MediCureOn Data Connected' : '⚠️ Offline - Will sync when connected'}
              </p>
            </div>
          </div>
        </div>

        {/* Today's Health Progress + Total MediPoints */}
        <div className="grid gap-4 mb-8 md:grid-cols-5">
          <div className="p-4 border border-green-100 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-xs font-medium text-green-700">COMPLETED</span>
            </div>
            <p className="text-2xl font-bold text-green-700">
              {dailyActivities.filter(activity => activity.claimedToday).length}
            </p>
            <p className="text-sm text-green-600">Activities Done</p>
          </div>
          
          <div className="p-4 border border-blue-100 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-5 h-5 text-blue-600" />
              <span className="text-xs font-medium text-blue-700">REMAINING</span>
            </div>
            <p className="text-2xl font-bold text-blue-700">
              {dailyActivities.filter(activity => !activity.claimedToday).length}
            </p>
            <p className="text-sm text-blue-600">Activities Left</p>
          </div>
          
          <div className="p-4 border border-purple-100 bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <Crown className="w-5 h-5 text-purple-600" />
              <span className="text-xs font-medium text-purple-700">EARNED TODAY</span>
            </div>
            <p className="text-2xl font-bold text-purple-700">
              {totalPointsEarnedToday}
            </p>
            <p className="text-sm text-purple-600">MediPoints</p>
          </div>
          
          <div className="p-4 border border-yellow-100 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <Database className="w-5 h-5 text-orange-600" />
              <span className="text-xs font-medium text-orange-700">TOTAL BALANCE</span>
            </div>
            <p className="text-2xl font-bold text-orange-700">
              {totalMediPoints.toLocaleString()}
            </p>
            <p className="text-sm text-orange-600">All MediPoints</p>
          </div>

          <div className="p-4 border bg-gradient-to-br from-cyan-50 to-teal-50 rounded-xl border-cyan-100">
            <div className="flex items-center justify-between mb-2">
              <Database className="w-5 h-5 text-cyan-600" />
              <span className="text-xs font-medium text-cyan-700">AZURE SYNC</span>
            </div>
            <p className="text-2xl font-bold text-cyan-700">
              {isBackendConnected ? '✅' : '⚠️'}
            </p>
            <p className="text-sm text-cyan-600">
              {isBackendConnected ? 'Connected' : 'Offline'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {dailyActivities.map((activity) => (
            <div 
              key={activity.id}
              className={`relative border-2 rounded-xl p-6 transition-all transform hover:scale-105 ${
                activity.claimedToday 
                  ? 'border-green-300 bg-gradient-to-br from-green-50 to-emerald-50 shadow-lg' 
                  : activity.dataAvailable 
                    ? 'border-blue-300 bg-gradient-to-br from-blue-50 to-indigo-50 hover:border-blue-400 shadow-lg' 
                    : 'border-gray-300 bg-gradient-to-br from-gray-50 to-slate-50 shadow-md'
              }`}
            >
              {/* Status Badge */}
              {activity.claimedToday && (
                <div className="absolute top-4 right-4">
                  <div className="flex items-center px-3 py-1 space-x-1 bg-green-100 rounded-full">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-xs font-bold text-green-700">CLAIMED</span>
                  </div>
                </div>
              )}

              {/* Device Connection Status */}
              {!activity.claimedToday && (
                <div className="absolute top-4 right-4">
                  <div className={`flex items-center space-x-1 px-3 py-1 rounded-full ${
                    activity.deviceConnected 
                      ? 'bg-blue-100 text-blue-700' 
                      : activity.dataType === 'mood' 
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-gray-100 text-gray-600'
                  }`}>
                    <div className={`w-2 h-2 rounded-full ${
                      activity.deviceConnected 
                        ? 'bg-blue-500' 
                        : activity.dataType === 'mood' 
                          ? 'bg-purple-500'
                          : 'bg-gray-400'
                    }`} />
                    <span className="text-xs font-medium">
                      {activity.deviceConnected ? 'CONNECTED' : activity.dataType === 'mood' ? 'MANUAL' : 'NO DATA'}
                    </span>
                  </div>
                </div>
              )}

              {/* Activity Header */}
              <div className="mb-4">
                <div className="flex items-center mb-2 space-x-3">
                  <span className="text-3xl">{activity.icon}</span>
                  <div>
                    <h4 className="text-lg font-bold text-gray-900">{activity.name}</h4>
                    <p className="text-sm text-gray-600">{activity.category}</p>
                  </div>
                </div>
                <p className="text-sm leading-relaxed text-gray-700">{activity.description}</p>
              </div>

              {/* Current Value Display */}
              <div className="p-3 mb-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-500">CURRENT STATUS</span>
                  <span className="text-xs font-medium text-gray-500">
                    {activity.points} POINTS
                  </span>
                </div>
                <p className="mt-1 text-sm font-medium text-gray-900">
                  {activity.currentValue}
                </p>
                {activity.lastDataTimestamp && (
                  <p className="mt-1 text-xs text-gray-500">
                    Last updated: {new Date(activity.lastDataTimestamp).toLocaleTimeString()}
                  </p>
                )}
              </div>

              {/* Data Sources */}
              <div className="mt-3">
                <p className="mb-1 text-xs font-medium text-gray-500">SUPPORTED DEVICES & APPS</p>
                <div className="flex flex-wrap gap-1">
                  {activity.dataSources.slice(0, 3).map((source, index) => (
                    <span key={index} className="inline-block px-2 py-1 text-xs text-gray-600 bg-gray-100 rounded">
                      {source}
                    </span>
                  ))}
                  {activity.dataSources.length > 3 && (
                    <span className="inline-block px-2 py-1 text-xs text-gray-600 bg-gray-100 rounded">
                      +{activity.dataSources.length - 3} more
                    </span>
                  )}
                </div>
              </div>

              {/* Bonus Information */}
              {activity.bonus && (
                <div className="p-2 mt-3 border border-yellow-200 rounded-lg bg-yellow-50">
                  <div className="flex items-start space-x-2">
                    <Lightbulb className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-yellow-800">{activity.bonus}</p>
                  </div>
                </div>
              )}
              
              {/* 🚀 MODERNIZED: Dynamic Action Button (No Manual Entry) */}
              <button
                onClick={() => claimDailyActivity(activity.id)}
                disabled={activity.claimedToday || !activity.dataAvailable || activity.isComingSoon || claimingStates[activity.id] === 'claiming'}
                className={`w-full mt-4 py-3 px-4 rounded-lg font-bold transition-all relative ${
                  activity.claimedToday
                    ? 'bg-green-100 text-green-700 cursor-not-allowed border border-green-200'
                    : activity.isComingSoon
                      ? 'bg-blue-100 text-blue-700 cursor-not-allowed border border-blue-200'
                      : activity.dataAvailable
                        ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 shadow-lg transform hover:scale-105'
                        : 'bg-gray-200 text-gray-500 cursor-not-allowed border border-gray-300'
                }`}
              >
                {claimingStates[activity.id] === 'claiming' ? (
                  <div className="flex items-center justify-center space-x-2">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Processing...</span>
                  </div>
                ) : claimingStates[activity.id] === 'success' ? (
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>Claimed!</span>
                  </div>
                ) : claimingStates[activity.id] === 'error' ? (
                  <div className="flex items-center justify-center space-x-2">
                    <AlertTriangle className="w-4 h-4" />
                    <span>Try Again</span>
                  </div>
                ) : activity.claimedToday ? (
                  '✓ Claimed Today - Available at Midnight'
                ) : activity.isComingSoon ? (
                  '🚧 Coming Soon in Phase 02'
                ) : activity.dataAvailable ? (
                  `🤖 Claim ${activity.points} MediPoints`
                ) : (
                  '⏳ Waiting for Device Data...'
                )}
              </button>

              {/* 🚀 MODERNIZED: Device Connection Help for All Activities */}
              {!activity.claimedToday && !activity.isComingSoon && !activity.deviceConnected && (
                <div className="p-3 mt-2 border border-orange-200 rounded-lg bg-orange-50">
                  <div className="flex items-start space-x-2">
                    <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="mb-1 text-xs font-medium text-orange-800">Device Connection Required</p>
                      <p className="text-xs text-orange-700">
                        Connect a {activity.dataSources[0]} or compatible device to automatically track {activity.dataType} and earn points.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* 🚧 PHASE 02: Coming Soon Information */}
              {activity.isComingSoon && (
                <div className="p-3 mt-2 border border-blue-200 rounded-lg bg-blue-50">
                  <div className="flex items-start space-x-2">
                    <Database className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="mb-1 text-xs font-medium text-blue-800">Phase 02 Feature</p>
                      <p className="text-xs text-blue-700">
                        {activity.id === 'blood_pressure_monitoring' 
                          ? 'Advanced cardiovascular risk assessment with continuous blood pressure monitoring and heart health analytics will be available in the next update.'
                          : 'Advanced nutrition tracking with intelligent meal recognition and macro analysis will be available in the next update.'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* One-Time Bonus Opportunities */}
      <div className="p-6 text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl">
        <h3 className="mb-2 text-xl font-bold">One-Time Bonus Rewards</h3>
        <p className="mb-6 text-purple-100">Complete these milestones to earn substantial bonus points! Points are awarded automatically when conditions are met.</p>
        
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {/* Complete Profile - Auto Award 500 Points */}
          <div className={`p-5 rounded-xl transition-all ${
            bonusOpportunities.profileComplete.claimed 
              ? 'bg-green-500/20 border border-green-300/50' 
              : bonusOpportunities.profileComplete.readyToAward
                ? 'bg-yellow-500/20 border border-yellow-300/50 animate-pulse'
                : 'bg-white/10 hover:bg-white/20 border border-white/20'
          }`}>
            <div className="flex items-center justify-between mb-3">
              <FileText className="w-8 h-8 text-white" />
              {bonusOpportunities.profileComplete.claimed ? (
                <CheckCircle className="w-6 h-6 text-green-300" />
              ) : bonusOpportunities.profileComplete.isLoading ? (
                <RefreshCw className="w-6 h-6 text-white animate-spin" />
              ) : bonusOpportunities.profileComplete.readyToAward ? (
                <Crown className="w-6 h-6 text-yellow-300 animate-bounce" />
              ) : (
                <Clock className="w-6 h-6 text-white/60" />
              )}
            </div>
            <h4 className="mb-2 font-bold text-white">Complete Your Profile</h4>
            <p className="mb-3 text-sm text-purple-100">
              {bonusOpportunities.profileComplete.isLoading 
                ? 'Checking MediCureOn Data files...'
                : bonusOpportunities.profileComplete.claimed
                  ? '✅ All 3 profile sections completed!'
                  : `Fill out all 3 profile sections (${bonusOpportunities.profileComplete.completionPercentage}% complete)`
              }
            </p>
            
            {/* Progress Indicator */}
            {!bonusOpportunities.profileComplete.claimed && !bonusOpportunities.profileComplete.isLoading && (
              <div className="mb-3">
                <div className="flex justify-between mb-1 text-xs text-purple-200">
                  <span>Progress</span>
                  <span>{bonusOpportunities.profileComplete.completionPercentage}%</span>
                </div>
                <div className="w-full h-2 rounded-full bg-white/20">
                  <div 
                    className="h-2 transition-all duration-500 rounded-full bg-gradient-to-r from-yellow-400 to-yellow-300"
                    style={{ width: `${bonusOpportunities.profileComplete.completionPercentage}%` }}
                  ></div>
                </div>
                <div className="mt-1 text-xs text-purple-200">
                  Files Found: {Object.values(bonusOpportunities.profileComplete.filesFound || {}).filter(Boolean).length}/3
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <span className="text-lg font-bold text-yellow-300">+500 points</span>
              {bonusOpportunities.profileComplete.claimed ? (
                <div className="px-4 py-2 font-medium text-green-200 rounded-lg bg-green-500/30">
                  ✅ Awarded!
                </div>
              ) : bonusOpportunities.profileComplete.readyToAward ? (
                <div className="px-4 py-2 font-medium text-yellow-200 rounded-lg bg-yellow-500/30 animate-pulse">
                  🎁 Awarding...
                </div>
              ) : (
                <button 
                  onClick={() => onNavigate && onNavigate('profile')}
                  className="px-4 py-2 font-medium text-purple-600 transition-all bg-white rounded-lg hover:bg-purple-50"
                >
                  Complete Profile
                </button>
              )}
            </div>
          </div>

          {/* First Healthcare Plan - Auto Award 300 Points */}
          <div className={`p-5 rounded-xl transition-all ${
            bonusOpportunities.firstPlan.claimed 
              ? 'bg-green-500/20 border border-green-300/50' 
              : bonusOpportunities.firstPlan.readyToAward
                ? 'bg-yellow-500/20 border border-yellow-300/50 animate-pulse'
                : 'bg-white/10 hover:bg-white/20 border border-white/20'
          }`}>
            <div className="flex items-center justify-between mb-3">
              <Target className="w-8 h-8 text-white" />
              {bonusOpportunities.firstPlan.claimed ? (
                <CheckCircle className="w-6 h-6 text-green-300" />
              ) : bonusOpportunities.firstPlan.isLoading ? (
                <RefreshCw className="w-6 h-6 text-white animate-spin" />
              ) : bonusOpportunities.firstPlan.readyToAward ? (
                <Crown className="w-6 h-6 text-yellow-300 animate-bounce" />
              ) : (
                <Clock className="w-6 h-6 text-white/60" />
              )}
            </div>
            <h4 className="mb-2 font-bold text-white">Join Healthcare Plan</h4>
            <p className="mb-3 text-sm text-purple-100">
              {bonusOpportunities.firstPlan.isLoading 
                ? 'Checking for active-plans.json...'
                : bonusOpportunities.firstPlan.claimed
                  ? '✅ Healthcare plan enrolled!'
                  : bonusOpportunities.firstPlan.planExists
                    ? '🎉 Plan detected - awarding bonus!'
                    : 'Enroll in your first healthcare plan'
              }
            </p>
            
            {/* Plan Status Indicator */}
            {!bonusOpportunities.firstPlan.claimed && !bonusOpportunities.firstPlan.isLoading && (
              <div className="mb-3">
                <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                  bonusOpportunities.firstPlan.planExists 
                    ? 'bg-green-500/30 text-green-200' 
                    : 'bg-orange-500/30 text-orange-200'
                }`}>
                  {bonusOpportunities.firstPlan.planExists ? '📋 Plan File Found' : '📋 No Plan File'}
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <span className="text-lg font-bold text-yellow-300">+300 points</span>
              {bonusOpportunities.firstPlan.claimed ? (
                <div className="px-4 py-2 font-medium text-green-200 rounded-lg bg-green-500/30">
                  ✅ Awarded!
                </div>
              ) : bonusOpportunities.firstPlan.readyToAward ? (
                <div className="px-4 py-2 font-medium text-yellow-200 rounded-lg bg-yellow-500/30 animate-pulse">
                  🎁 Awarding...
                </div>
              ) : (
                <button 
                  onClick={() => onNavigate && onNavigate('plans')}
                  className="px-4 py-2 font-medium text-purple-600 transition-all bg-white rounded-lg hover:bg-purple-50"
                >
                  View Plans
                </button>
              )}
            </div>
          </div>

          {/* Referral Program - Phase 2 Coming Soon */}
          <div className="p-5 transition-all border bg-gray-500/20 border-gray-400/30 rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <Users className="w-8 h-8 text-gray-300" />
              <div className="px-2 py-1 text-xs font-bold text-blue-600 bg-blue-200 rounded-full">
                Phase 2
              </div>
            </div>
            <h4 className="mb-2 font-bold text-gray-200">Refer Friends</h4>
            <p className="mb-3 text-sm text-gray-300">
              Coming Soon in Phase 2! Refer friends and earn bonus points.
            </p>
            
            {/* Coming Soon Badge */}
            <div className="mb-3">
              <div className="inline-block px-3 py-1 text-xs font-medium text-blue-200 rounded-full bg-blue-500/30">
                🚀 Coming Soon - Phase 2 Feature
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-lg font-bold text-gray-400">100 pts each</span>
              <button 
                disabled
                className="px-4 py-2 font-medium text-gray-300 bg-gray-500 rounded-lg cursor-not-allowed"
              >
                Coming Soon
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EarnPointsTab;
